import React, { useState, useEffect, useRef, useCallback, useMemo, Fragment } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Badge,
  useToast,
  Icon,
  Box,
  Progress,
  Alert,
  AlertIcon,
  Divider,
  useColorModeValue,
  SimpleGrid,
  Tooltip,
} from '@chakra-ui/react';
import {
  FiUpload,
  FiCheckCircle,
  FiAlertCircle,
  FiDownload,
  FiFileText,
  FiGrid,
  FiSearch,
  FiPlayCircle,
  FiFlag,
  FiDatabase,
} from 'react-icons/fi';
import { useAuth } from '../../../../contexts/AuthContext';
import { useCrmSettings } from '../../../../contexts/CrmSettingsContext';
import { supabase } from '../../../../utils/supabase';
import { useAutoTour } from '../../../FeatureTour/FeatureTourProvider';
import { TourReplayButton } from '../../../FeatureTour/TourHelpButton';

import {
  detectRetrFormat,
  detectColumnMapping,
  detectImportKind,
  isFieldConsumed,
  validateMappedData,
  buildMappedRecords,
} from '../importMapping';
import useSpreadsheetParser from '../useSpreadsheetParser';
import useImportRunner from '../useImportRunner';
import {
  findContactDuplicates,
  findDealDuplicates,
  collectContactTypeValues,
  applyContactTypeMapping,
} from '../importPreview';
import {
  downloadTemplateCsv,
  loadRememberedMapping,
  saveRememberedMapping,
} from './studioFields';
import StudioMatchingBoard from './StudioMatchingBoard';
import StudioReviewStep from './StudioReviewStep';

const STEPS = [
  { title: 'Upload', description: 'Pick your file', icon: FiUpload },
  { title: 'Match', description: 'Match columns', icon: FiGrid },
  { title: 'Review', description: 'Duplicates & checks', icon: FiSearch },
  { title: 'Import', description: 'Run the import', icon: FiPlayCircle },
  { title: 'Done', description: 'Results', icon: FiFlag },
];

/**
 * Import Studio — the full-width import experience for all CRM users.
 * Shares the exact parsing / mapping / validation / execution pipeline with
 * ImportCSVModal (importMapping.js, useSpreadsheetParser, useImportRunner);
 * adds the visual Matching Board, duplicate preview with per-row overwrite
 * control, templates, and mapping memory.
 *
 * Props match ImportCSVModal so the ImportModal wrapper can swap freely.
 */
export default function ImportStudioModal({
  isOpen,
  onClose,
  importType = 'contacts',
  autoAssignTo = null,
  allowAssignmentSelection = false,
  onImportComplete,
  crmUsers = [],
}) {
  const { currentUser, SuperAdmin, Roles } = useAuth();
  const { contactTypes: crmContactTypes, dealStages, getVisibleTags } = useCrmSettings();
  const isCRMAdmin = SuperAdmin || Roles?.includes('CRM Admin');
  const availableTags = getVisibleTags(currentUser?.uid, isCRMAdmin) || [];
  const toast = useToast();
  const fileInputRef = useRef(null);

  // Which page the importer was opened from only SEEDS the kind — the
  // uploaded file's columns decide (people constantly import deals from the
  // contacts page and vice versa). See detectImportKind + the board banner.
  const seededKind = importType === 'deals' ? 'deals' : 'contacts';

  // ------------------------------------------------------------------ state
  const [currentStep, setCurrentStep] = useState(0);
  const [csvData, setCsvData] = useState(null);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [excelSheets, setExcelSheets] = useState(null);
  const [sourceFileName, setSourceFileName] = useState('');
  const [columnMapping, setColumnMapping] = useState({});
  const [restoredMapping, setRestoredMapping] = useState(false);
  const [isRetrImport, setIsRetrImport] = useState(false);
  const [retrState, setRetrState] = useState('');
  const [selectedContactTypes, setSelectedContactTypes] = useState([]);
  const [globalTags, setGlobalTags] = useState([]);
  const [assignToId, setAssignToId] = useState(autoAssignTo || '');
  // Admin-only: import everything in this file as company records
  const [importAsCompany, setImportAsCompany] = useState(false);
  // Candidates are company records by policy: selecting the Candidate default
  // type locks the scope switch on. The server mirrors this rule per-row for
  // admin imports, covering candidate values mapped from CSV columns too.
  const importHasCandidateType = selectedContactTypes.includes('candidate');
  const [isDragging, setIsDragging] = useState(false);
  const [activeKind, setActiveKind] = useState(seededKind); // 'contacts' | 'deals'
  const [kindDetection, setKindDetection] = useState(null); // { kind, confidence, dealHeaders, autoSwitched }
  const [typeValueMap, setTypeValueMap] = useState({}); // lowercased CSV value -> configured type slug

  const isDealsImport = activeKind === 'deals';
  // The importType sent through records/payload. 'contacts-with-assignment'
  // keeps its assignment semantics when the file stays a contacts list.
  const activeImportType = isDealsImport
    ? 'deals'
    : (importType === 'contacts-with-assignment' ? 'contacts-with-assignment' : 'contacts');

  // Validation (computed entering Review)
  const [validation, setValidation] = useState(null); // { fileErrors, rowErrors, warnings, badRowIndexes }
  const [skipInvalidRows, setSkipInvalidRows] = useState(false);

  // Duplicate preview (async, advisory — backend re-checks at import time)
  const [preview, setPreview] = useState({ status: 'idle', contactMatches: [], dealMatches: [], records: [] });
  const [rowDecisions, setRowDecisions] = useState({}); // previewIdx -> 'update' | 'skip'
  const [globalOverwrite, setGlobalOverwrite] = useState(false);

  const [importResults, setImportResults] = useState(null);

  const { importing, importProgress, progressText, runImport, resetRunner } = useImportRunner();

  // First-open walkthrough (same guards as route auto-start: seen-state,
  // audience, one attempt per session). Replayable via the header button.
  useAutoTour(isOpen, 'import-studio');

  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const cardBg = useColorModeValue('white', 'gray.800');
  const subtleBg = useColorModeValue('gray.50', 'gray.750');
  const dropzoneHoverBg = useColorModeValue('blue.50', 'rgba(66, 153, 225, 0.08)');
  const stepInactiveBg = useColorModeValue('gray.200', 'gray.600');

  const resetState = useCallback(() => {
    setCurrentStep(0);
    setCsvData(null);
    setCsvHeaders([]);
    setExcelSheets(null);
    setSourceFileName('');
    setColumnMapping({});
    setRestoredMapping(false);
    setIsRetrImport(false);
    setRetrState('');
    setSelectedContactTypes([]);
    setGlobalTags([]);
    setAssignToId(autoAssignTo || '');
    setImportAsCompany(false);
    setIsDragging(false);
    setActiveKind(seededKind);
    setKindDetection(null);
    setTypeValueMap({});
    setValidation(null);
    setSkipInvalidRows(false);
    setPreview({ status: 'idle', contactMatches: [], dealMatches: [], records: [] });
    setRowDecisions({});
    setGlobalOverwrite(false);
    setImportResults(null);
    resetRunner();
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [autoAssignTo, seededKind, resetRunner]);

  useEffect(() => {
    if (!isOpen) resetState();
  }, [isOpen, resetState]);

  // ------------------------------------------------------------------ upload
  const ingestParsedData = useCallback((rows, headers, formatLabel) => {
    if (!rows || rows.length === 0 || !headers || headers.length === 0) {
      toast({ title: 'No data found', description: 'The file has no data rows with headers.', status: 'warning', duration: 4000 });
      return;
    }

    // Mirrors the backend guard (callable payload/runtime limits)
    const MAX_IMPORT_ROWS = 10000;
    if (rows.length > MAX_IMPORT_ROWS) {
      toast({
        title: 'File too large',
        description: `This file has ${rows.length.toLocaleString()} rows — the maximum per import is ${MAX_IMPORT_ROWS.toLocaleString()}. Split the file and import it in batches.`,
        status: 'error',
        duration: 8000,
      });
      return;
    }

    setCsvData(rows);
    setCsvHeaders(headers);
    setExcelSheets(null);
    setTypeValueMap({});

    const isRetr = detectRetrFormat(headers);
    setIsRetrImport(isRetr);

    // Decide contacts-vs-deals from the file itself, regardless of which
    // page the importer was opened from.
    let nextKind = seededKind;
    if (isRetr) {
      setKindDetection(null);
    } else {
      const detection = detectImportKind(headers);
      nextKind = detection.kind;
      setKindDetection({ ...detection, autoSwitched: detection.kind !== seededKind });
    }
    setActiveKind(nextKind);
    const nextImportType = nextKind === 'deals'
      ? 'deals'
      : (importType === 'contacts-with-assignment' ? 'contacts-with-assignment' : 'contacts');

    // Mapping memory first; fall back to auto-detection. Only consumed
    // fields are mapped — everything else flows into Imported Data.
    const remembered = loadRememberedMapping(headers, isRetr ? 'retr' : nextKind);
    if (remembered) {
      const filtered = Object.fromEntries(
        Object.entries(remembered).filter(([field]) => isFieldConsumed(field, isRetr, nextImportType))
      );
      setColumnMapping(filtered);
      setRestoredMapping(true);
    } else {
      const rawMapping = detectColumnMapping(headers, { importType: nextImportType, isRetrImport: isRetr });
      const autoMapping = Object.fromEntries(
        Object.entries(rawMapping).filter(([field]) => isFieldConsumed(field, isRetr, nextImportType))
      );
      setColumnMapping(autoMapping);
      setRestoredMapping(false);
    }

    if (isRetr) {
      const brokerType = crmContactTypes?.find(t =>
        t.value.toLowerCase().includes('broker') || t.value.toLowerCase().includes('mortgage')
      );
      if (brokerType) setSelectedContactTypes([brokerType.value]);

      const firstRow = rows[0] || {};
      const firstLocation = firstRow['LO Location'] || firstRow['office_location'] || '';
      const stateMatch = String(firstLocation).match(/,\s*([A-Z]{2})\s*\d{0,5}$/);
      if (stateMatch) setRetrState(stateMatch[1]);
    }

    toast({
      title: `${formatLabel} loaded`,
      description: `Found ${rows.length.toLocaleString()} rows`,
      status: 'success',
      duration: 2000,
    });

    setCurrentStep(1);
  }, [crmContactTypes, importType, seededKind, toast]);

  const { parseFile } = useSpreadsheetParser({
    onData: ingestParsedData,
    onSheets: setExcelSheets,
    onError: toast,
  });

  const processFile = useCallback((file) => {
    if (!file) return;
    setSourceFileName(file.name);
    parseFile(file);
  }, [parseFile]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer?.files?.[0];
    const fileName = file?.name?.toLowerCase() || '';
    if (file && ['.csv', '.xlsx', '.xls'].some(ext => fileName.endsWith(ext))) {
      processFile(file);
    } else if (file) {
      toast({ title: 'Invalid file type', description: 'Please drop a .csv, .xlsx, or .xls file', status: 'warning', duration: 3000 });
    }
  }, [processFile, toast]);

  // Manual contacts/deals override from the board banner. Remaps columns for
  // the new kind (remembered mapping first, auto-detection otherwise).
  const switchKind = useCallback((newKind) => {
    setActiveKind(newKind);
    setKindDetection(prev => (prev ? { ...prev, autoSwitched: false } : prev));
    setRowDecisions({});
    const nextImportType = newKind === 'deals'
      ? 'deals'
      : (importType === 'contacts-with-assignment' ? 'contacts-with-assignment' : 'contacts');
    const remembered = loadRememberedMapping(csvHeaders, newKind);
    if (remembered) {
      setColumnMapping(Object.fromEntries(
        Object.entries(remembered).filter(([field]) => isFieldConsumed(field, false, nextImportType))
      ));
      setRestoredMapping(true);
    } else {
      const rawMapping = detectColumnMapping(csvHeaders, { importType: nextImportType, isRetrImport: false });
      setColumnMapping(Object.fromEntries(
        Object.entries(rawMapping).filter(([field]) => isFieldConsumed(field, false, nextImportType))
      ));
      setRestoredMapping(false);
    }
  }, [csvHeaders, importType]);

  // Unique values in the mapped Contact Type column + their auto-resolution
  // (feeds the board's "Match your contact types" card).
  const typeValues = useMemo(() => {
    if (isRetrImport || !csvData || !columnMapping.contactTypes) return [];
    return collectContactTypeValues(csvData, columnMapping.contactTypes, crmContactTypes);
  }, [isRetrImport, csvData, columnMapping.contactTypes, crmContactTypes]);

  // ------------------------------------------------------- validation gating
  const invalidRowIndexes = validation?.badRowIndexes || new Set();
  const mappingErrors = validation?.fileErrors || [];
  const validRowCount = (csvData?.length || 0) - invalidRowIndexes.size;

  const importableRows = useMemo(() => {
    if (!csvData) return [];
    if (skipInvalidRows && invalidRowIndexes.size > 0) {
      return csvData.filter((_, idx) => !invalidRowIndexes.has(idx));
    }
    return csvData;
  }, [csvData, skipInvalidRows, invalidRowIndexes]);

  const runValidation = useCallback(() => {
    const result = validateMappedData({ csvData, columnMapping, isRetrImport, retrState });
    setValidation(result);
    setSkipInvalidRows(false);
    return result;
  }, [csvData, columnMapping, isRetrImport, retrState]);

  // -------------------------------------------------------- duplicate preview
  const previewOwnerId = assignToId || autoAssignTo || currentUser?.uid;

  useEffect(() => {
    // Preview runs on the Review step for standard imports (RETR upserts by
    // profile URL server-side; no preview needed).
    if (currentStep !== 2 || isRetrImport || !csvData) return;
    // Row-level validity affects which rows get sent — preview mirrors it
    const rows = importableRows;
    if (rows.length === 0) {
      setPreview({ status: 'ready', contactMatches: [], dealMatches: [], records: [] });
      return;
    }

    let cancelled = false;
    (async () => {
      setPreview(prev => ({ ...prev, status: 'loading' }));
      try {
        const records = applyContactTypeMapping(buildMappedRecords({
          rows,
          csvHeaders,
          columnMapping,
          importType: activeImportType,
          isRetrImport: false,
          retrState: '',
          globalTags: globalTags.map(o => o.value),
        }), typeValueMap);
        if (isDealsImport) {
          const { dealMatches, contactMatches } = await findDealDuplicates(supabase, records, previewOwnerId);
          if (!cancelled) setPreview({ status: 'ready', contactMatches, dealMatches, records });
        } else {
          const contactMatches = await findContactDuplicates(supabase, records, previewOwnerId);
          if (!cancelled) setPreview({ status: 'ready', contactMatches, dealMatches: [], records });
        }
      } catch (error) {
        console.error('Duplicate preview failed:', error);
        if (!cancelled) setPreview({ status: 'error', contactMatches: [], dealMatches: [], records: [] });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, isRetrImport, csvData, importableRows, csvHeaders, columnMapping, activeImportType, isDealsImport, previewOwnerId, typeValueMap]);

  // Reset per-row decisions when the preview inputs change
  useEffect(() => { setRowDecisions({}); }, [csvData, skipInvalidRows, previewOwnerId]);

  // ------------------------------------------------------------------ import
  const decisionFor = useCallback((idx, match) => {
    if (match?.isCrossOwner) return 'skip'; // never updatable
    return rowDecisions[idx] || (globalOverwrite ? 'update' : 'skip');
  }, [rowDecisions, globalOverwrite]);

  const handleImport = async () => {
    try {
      const effectiveImportType = isRetrImport ? 'retr-contacts' : activeImportType;
      const functionName = (isRetrImport || !isDealsImport) ? 'importContacts' : 'importDeals';

      const skippedInvalidCount = (csvData?.length || 0) - importableRows.length;

      const mappedData = applyContactTypeMapping(buildMappedRecords({
        rows: importableRows,
        csvHeaders,
        columnMapping,
        importType: activeImportType,
        isRetrImport,
        retrState,
        globalTags: globalTags.map(o => o.value),
      }), isRetrImport ? {} : typeValueMap);

      // Attach per-row overwrite flags for every previewed duplicate. Rows
      // the preview didn't flag fall back to duplicateStrategy server-side.
      if (!isRetrImport && preview.status === 'ready') {
        const matches = isDealsImport ? preview.dealMatches : preview.contactMatches;
        matches.forEach((match, idx) => {
          if (!match || match.inFileRepeat || !mappedData[idx]) return;
          mappedData[idx].overwrite = decisionFor(idx, match) === 'update';
        });
      }

      const results = await runImport({
        functionName,
        payload: {
          records: mappedData,
          assignToId: assignToId || autoAssignTo,
          importType: effectiveImportType,
          retrState: isRetrImport ? retrState : undefined,
          contactTypes: selectedContactTypes.length > 0 ? selectedContactTypes : undefined,
          fileName: sourceFileName || undefined,
          duplicateStrategy: !isRetrImport && globalOverwrite ? 'update' : 'skip',
          recordScope: isCRMAdmin && (importAsCompany || importHasCandidateType) ? 'company' : undefined,
        },
        totalRows: mappedData.length,
      });

      if (results.success) {
        const resultsWithSkipped = { ...results, skippedInvalidRows: skippedInvalidCount };
        setImportResults(resultsWithSkipped);
        saveRememberedMapping(csvHeaders, isRetrImport ? 'retr' : activeKind, columnMapping);
        setCurrentStep(4);
        if (onImportComplete) onImportComplete(resultsWithSkipped);
      }
    } catch (error) {
      console.error('Import error:', error);
      toast({
        title: 'Import failed',
        description: `${error.message}${error.mayStillBeRunning ? ' — check Import History before re-uploading to avoid duplicates.' : ''}`,
        status: 'error',
        duration: 8000,
      });
    }
  };

  // ------------------------------------------------------------- step gating
  const canProceed = () => {
    if (currentStep === 0) return csvData !== null;
    if (currentStep === 1) {
      if (isRetrImport) {
        const hasLoName = !!columnMapping.loName;
        const hasFirstLast = !!columnMapping.firstName && !!columnMapping.lastName;
        return (hasLoName || hasFirstLast) && !!retrState;
      }
      return !!columnMapping.firstName && !!columnMapping.lastName &&
        (!!columnMapping.email || !!columnMapping.phone);
    }
    if (currentStep === 2) {
      if (mappingErrors.length > 0) return false;
      if (invalidRowIndexes.size === 0) return true;
      return skipInvalidRows && validRowCount > 0;
    }
    return false;
  };

  const handleNext = () => {
    if (currentStep === 1) {
      runValidation();
      setCurrentStep(2);
      return;
    }
    if (currentStep === 2) {
      if (mappingErrors.length > 0) {
        toast({ title: 'Cannot proceed', description: 'Fix the field mapping issues before importing', status: 'error', duration: 3000 });
        return;
      }
      if (invalidRowIndexes.size > 0 && (!skipInvalidRows || validRowCount === 0)) {
        toast({
          title: 'Cannot proceed',
          description: validRowCount === 0
            ? 'No valid rows to import'
            : `${invalidRowIndexes.size} rows have errors — fix the file or check "Import valid rows only"`,
          status: 'error',
          duration: 4000,
        });
        return;
      }
      setCurrentStep(3);
      return;
    }
    if (currentStep < STEPS.length - 1) setCurrentStep(currentStep + 1);
  };

  const handleBack = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  // ------------------------------------------------------------------ render
  const entityLabel = isDealsImport ? 'deals' : 'contacts';

  const renderStepper = () => (
    <HStack spacing={0} justify="center" data-tour="studio-stepper">
      {STEPS.map((step, index) => (
        <Fragment key={step.title}>
          <VStack spacing={1} minW="88px">
            <Box
              w="34px"
              h="34px"
              borderRadius="full"
              bg={index === currentStep ? 'blue.500' : index < currentStep ? 'green.500' : stepInactiveBg}
              color="white"
              display="flex"
              alignItems="center"
              justifyContent="center"
              fontWeight="bold"
              fontSize="sm"
              transition="background 0.2s"
            >
              {index < currentStep ? <Icon as={FiCheckCircle} /> : <Icon as={step.icon} />}
            </Box>
            <VStack spacing={0}>
              <Text fontSize="xs" fontWeight={index === currentStep ? 'bold' : 'medium'}>{step.title}</Text>
              <Text fontSize="2xs" color="gray.500">{step.description}</Text>
            </VStack>
          </VStack>
          {index < STEPS.length - 1 && (
            <Box h="2px" w={{ base: '16px', md: '56px' }} mb={7} bg={index < currentStep ? 'green.500' : stepInactiveBg} />
          )}
        </Fragment>
      ))}
    </HStack>
  );

  const renderUploadStep = () => {
    if (excelSheets && excelSheets.length > 1) {
      return (
        <VStack spacing={4} align="stretch" maxW="640px" mx="auto">
          <Alert status="info" borderRadius="md">
            <AlertIcon />
            <Text fontSize="sm">This workbook has {excelSheets.length} sheets with data. Choose the one to import.</Text>
          </Alert>
          <VStack spacing={2} align="stretch">
            {excelSheets.map(sheet => (
              <Button
                key={sheet.name}
                variant="outline"
                justifyContent="space-between"
                rightIcon={<Badge colorScheme="blue">{sheet.rows.length.toLocaleString()} rows</Badge>}
                onClick={() => ingestParsedData(sheet.rows, sheet.headers, `Excel sheet "${sheet.name}"`)}
              >
                {sheet.name}
              </Button>
            ))}
          </VStack>
          <Button variant="ghost" size="sm" onClick={() => setExcelSheets(null)}>
            Choose a different file
          </Button>
        </VStack>
      );
    }

    return (
      <VStack spacing={5} align="stretch" maxW="760px" mx="auto">
        <Box
          p={{ base: 8, md: 14 }}
          borderWidth="2px"
          borderStyle="dashed"
          borderColor={isDragging ? 'blue.400' : borderColor}
          borderRadius="xl"
          textAlign="center"
          cursor="pointer"
          bg={isDragging ? dropzoneHoverBg : 'transparent'}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
          onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); }}
          onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); if (!e.currentTarget.contains(e.relatedTarget)) setIsDragging(false); }}
          onDrop={handleDrop}
          _hover={{ borderColor: 'blue.400', bg: dropzoneHoverBg }}
          transition="all 0.2s"
          data-tour="studio-dropzone"
        >
          <Icon as={FiUpload} boxSize={14} color={isDragging ? 'blue.400' : 'gray.400'} mb={4} />
          <Text fontWeight="bold" fontSize="lg" mb={1}>
            {isDragging ? 'Drop your file here' : `Drag & drop your ${entityLabel} file`}
          </Text>
          <Text fontSize="sm" color="gray.500">
            or click to browse — .csv, .xlsx, and .xls up to 10,000 rows
          </Text>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={(e) => processFile(e.target.files[0])}
            style={{ display: 'none' }}
          />
        </Box>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} data-tour="studio-templates">
          <Box p={4} borderWidth="1px" borderColor={borderColor} borderRadius="lg" bg={cardBg}>
            <HStack mb={2}>
              <Icon as={FiFileText} color="blue.400" />
              <Text fontWeight="semibold" fontSize="sm">Not sure how to format your file?</Text>
            </HStack>
            <Text fontSize="xs" color="gray.500" mb={3}>
              Download a starter template with every column Origin understands, plus one example row. Columns you leave out are fine — and extra columns are never lost.
            </Text>
            <HStack>
              <Button size="xs" leftIcon={<FiDownload />} variant="outline" onClick={() => downloadTemplateCsv('contacts')}>
                Contacts template
              </Button>
              <Button size="xs" leftIcon={<FiDownload />} variant="outline" onClick={() => downloadTemplateCsv('deals')}>
                Deals template
              </Button>
            </HStack>
          </Box>
          <Box p={4} borderWidth="1px" borderColor={borderColor} borderRadius="lg" bg={cardBg}>
            <HStack mb={2}>
              <Icon as={FiDatabase} color="purple.400" />
              <Text fontWeight="semibold" fontSize="sm">The only must-haves</Text>
            </HStack>
            <Text fontSize="xs" color="gray.500">
              First name, last name, and either an email or a phone per row — that's it.
              We read your file's columns and figure out whether it's a contacts list or a
              deals list automatically (you can always override). Everything else — including
              columns Origin doesn't have a field for — is kept on the record under "Imported Data".
            </Text>
          </Box>
        </SimpleGrid>
      </VStack>
    );
  };

  const renderImportStep = () => (
    <VStack spacing={5} align="stretch" maxW="640px" mx="auto" data-tour="studio-run">
      {importing ? (
        <>
          <Text fontWeight="semibold" fontSize="lg" textAlign="center">
            Importing {importableRows.length.toLocaleString()} rows…
          </Text>
          <Progress
            value={importProgress}
            isIndeterminate={importProgress === 0}
            size="lg"
            colorScheme="blue"
            borderRadius="md"
            hasStripe
            isAnimated
          />
          <Text fontSize="sm" color="gray.600" textAlign="center">
            {progressText || 'Please wait, this may take a few moments…'}
          </Text>
          <Alert status="info" borderRadius="md" fontSize="xs">
            <AlertIcon />
            Keep this tab open. Large files can take a few minutes — if the connection drops, the import keeps running on the server and this screen recovers automatically.
          </Alert>
        </>
      ) : (
        <>
          <Text fontWeight="bold" fontSize="lg" textAlign="center">Ready when you are</Text>
          <Box p={5} borderWidth="1px" borderColor={borderColor} borderRadius="lg" bg={subtleBg}>
            <VStack align="stretch" spacing={2} fontSize="sm">
              <HStack justify="space-between">
                <Text>File</Text>
                <Text fontWeight="semibold" isTruncated maxW="320px">{sourceFileName || '—'}</Text>
              </HStack>
              <HStack justify="space-between">
                <Text>Rows to send</Text>
                <Badge colorScheme="blue">{importableRows.length.toLocaleString()}</Badge>
              </HStack>
              <HStack justify="space-between">
                <Text>Import type</Text>
                <Badge colorScheme={isRetrImport ? 'teal' : 'purple'}>
                  {isRetrImport ? `Loan Officers (${retrState})` : entityLabel}
                </Badge>
              </HStack>
              <HStack justify="space-between">
                <Text>Assign to</Text>
                <Badge colorScheme="green">
                  {(assignToId && crmUsers.find(u => u.uid === assignToId)?.name) || 'Me'}
                </Badge>
              </HStack>
              {!isRetrImport && (
                <HStack justify="space-between">
                  <Text>Duplicates</Text>
                  <Badge colorScheme={globalOverwrite ? 'blue' : 'yellow'}>
                    {globalOverwrite ? 'Update with spreadsheet data' : 'Skip (keep existing)'}
                  </Badge>
                </HStack>
              )}
            </VStack>
          </Box>
          <Button
            colorScheme="green"
            size="lg"
            onClick={handleImport}
            leftIcon={<FiUpload />}
            data-tour="studio-start-import"
          >
            Start Import
          </Button>
        </>
      )}
    </VStack>
  );

  const renderResultsStep = () => {
    const r = importResults || {};
    const inserted = r.insertedCount ?? r.successCount ?? 0;
    const updated = r.updatedCount ?? 0;
    const unmappedCount = csvHeaders.filter(h => !new Set(Object.values(columnMapping).filter(Boolean)).has(h)).length;
    const stats = [
      { label: 'Created', value: inserted, color: 'green' },
      { label: 'Updated', value: updated, color: 'blue' },
      { label: 'Duplicates skipped', value: r.duplicatesFound || 0, color: 'yellow' },
      { label: 'Errors', value: r.errorCount || 0, color: r.errorCount > 0 ? 'red' : 'gray' },
      { label: 'Warnings', value: r.warningCount || 0, color: r.warningCount > 0 ? 'orange' : 'gray' },
      { label: 'Invalid rows left out', value: r.skippedInvalidRows || 0, color: 'gray' },
    ];

    return (
      <VStack spacing={5} align="stretch" maxW="720px" mx="auto" data-tour="studio-results">
        <VStack spacing={2}>
          <Icon
            as={r.errorCount > 0 ? FiAlertCircle : FiCheckCircle}
            boxSize={14}
            color={r.errorCount > 0 ? 'orange.400' : 'green.400'}
          />
          <Text fontSize="xl" fontWeight="bold">
            {r.errorCount > 0 ? 'Import completed with some issues' : 'Import complete'}
          </Text>
          <Text fontSize="sm" color="gray.500">{r.message}</Text>
        </VStack>

        <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3}>
          {stats.map(stat => (
            <Box key={stat.label} p={3} borderWidth="1px" borderColor={borderColor} borderRadius="lg" textAlign="center" bg={cardBg}>
              <Text fontSize="2xl" fontWeight="bold" color={`${stat.color}.500`}>{Number(stat.value).toLocaleString()}</Text>
              <Text fontSize="xs" color="gray.500">{stat.label}</Text>
            </Box>
          ))}
        </SimpleGrid>

        {unmappedCount > 0 && (
          <Alert status="info" borderRadius="md" fontSize="sm">
            <AlertIcon />
            {unmappedCount} unmatched column{unmappedCount > 1 ? 's were' : ' was'} preserved on each record — open any imported {isDealsImport ? 'deal' : 'contact'} and look for the "Imported Data" section.
          </Alert>
        )}

        {(r.errors?.length > 0 || r.warnings?.length > 0) && (
          <VStack align="stretch" spacing={3}>
            {r.errors?.length > 0 && (
              <Box>
                <Text fontWeight="semibold" fontSize="sm" color="red.500" mb={1}>Errors</Text>
                <Box maxH="160px" overflowY="auto" p={3} borderWidth="1px" borderColor="red.200" borderRadius="md" fontSize="xs">
                  {r.errors.slice(0, 25).map((e, i) => <Text key={i} color="red.500">• {e}</Text>)}
                  {r.errors.length > 25 && <Text color="gray.500">… and {r.errors.length - 25} more (full list in Import History)</Text>}
                </Box>
              </Box>
            )}
            {r.warnings?.length > 0 && (
              <Box>
                <Text fontWeight="semibold" fontSize="sm" color="orange.500" mb={1}>Warnings</Text>
                <Box maxH="160px" overflowY="auto" p={3} borderWidth="1px" borderColor="orange.200" borderRadius="md" fontSize="xs">
                  {r.warnings.slice(0, 25).map((w, i) => <Text key={i} color="orange.500">• {w}</Text>)}
                  {r.warnings.length > 25 && <Text color="gray.500">… and {r.warnings.length - 25} more (full list in Import History)</Text>}
                </Box>
              </Box>
            )}
          </VStack>
        )}

        <Text fontSize="xs" color="gray.500" textAlign="center">
          {isCRMAdmin
            ? 'Every import is recorded — see CRM Admin → Data Management → Import History for the full log.'
            : 'Every import is recorded in the import log, and skipped rows are listed above.'}
        </Text>
      </VStack>
    );
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return renderUploadStep();
      case 1:
        return (
          <StudioMatchingBoard
            importType={activeImportType}
            isRetrImport={isRetrImport}
            retrState={retrState}
            setRetrState={setRetrState}
            csvData={csvData}
            csvHeaders={csvHeaders}
            columnMapping={columnMapping}
            setColumnMapping={setColumnMapping}
            restoredMapping={restoredMapping}
            crmContactTypes={crmContactTypes}
            selectedContactTypes={selectedContactTypes}
            setSelectedContactTypes={setSelectedContactTypes}
            availableTags={availableTags}
            globalTags={globalTags}
            setGlobalTags={setGlobalTags}
            allowAssignmentSelection={allowAssignmentSelection}
            crmUsers={crmUsers}
            assignToId={assignToId}
            setAssignToId={setAssignToId}
            allowCompanyScope={isCRMAdmin && !isRetrImport}
            importAsCompany={importAsCompany}
            setImportAsCompany={setImportAsCompany}
            companyScopeLocked={importHasCandidateType}
            kindDetection={kindDetection}
            activeKind={activeKind}
            onSwitchKind={switchKind}
            typeValues={typeValues}
            typeValueMap={typeValueMap}
            setTypeValueMap={setTypeValueMap}
          />
        );
      case 2:
        return (
          <StudioReviewStep
            importType={activeImportType}
            isDealsImport={isDealsImport}
            isRetrImport={isRetrImport}
            csvData={csvData}
            csvHeaders={csvHeaders}
            columnMapping={columnMapping}
            validation={validation}
            skipInvalidRows={skipInvalidRows}
            setSkipInvalidRows={setSkipInvalidRows}
            preview={preview}
            rowDecisions={rowDecisions}
            setRowDecisions={setRowDecisions}
            globalOverwrite={globalOverwrite}
            setGlobalOverwrite={setGlobalOverwrite}
            decisionFor={decisionFor}
            crmContactTypes={crmContactTypes}
            dealStages={dealStages}
            crmUsers={crmUsers}
            previewOwnerId={previewOwnerId}
            importableRowCount={importableRows.length}
          />
        );
      case 3:
        return renderImportStep();
      case 4:
        return renderResultsStep();
      default:
        return null;
    }
  };

  const nextLabel = currentStep === 1 ? 'Review' : currentStep === 2 ? 'Continue to Import' : 'Next';

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" closeOnOverlayClick={!importing} scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent
        maxH="92vh"
        minH="70vh"
        display="flex"
        flexDirection="column"
        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
        onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); if (currentStep === 0) setIsDragging(true); }}
        onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); if (!e.currentTarget.contains(e.relatedTarget)) setIsDragging(false); }}
        onDrop={(e) => { e.preventDefault(); e.stopPropagation(); if (currentStep === 0) handleDrop(e); }}
        data-tour="studio-modal"
      >
        <ModalHeader flexShrink={0} pb={2}>
          <HStack justify="space-between" pr={10}>
            <VStack align="flex-start" spacing={0}>
              <Text>
                {isRetrImport ? 'Import Studio — Loan Officers' : `Import Studio — ${isDealsImport ? 'Deals' : 'Contacts'}`}
              </Text>
              {sourceFileName && currentStep > 0 && (
                <Text fontSize="xs" fontWeight="normal" color="gray.500">
                  {sourceFileName} · {csvData?.length?.toLocaleString() || 0} rows · {csvHeaders.length} columns
                </Text>
              )}
            </VStack>
            <HStack spacing={1}>
              <TourReplayButton tourId="import-studio" size="xs">Tour</TourReplayButton>
            </HStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton isDisabled={importing} />

        <ModalBody overflowY="auto" flex="1 1 auto" pt={2}>
          <VStack spacing={6} align="stretch">
            {renderStepper()}
            <Divider />
            {renderStepContent()}
          </VStack>
        </ModalBody>

        <ModalFooter flexShrink={0} borderTopWidth="1px" borderColor={borderColor}>
          <HStack spacing={3}>
            <Button
              variant="ghost"
              onClick={currentStep === 0 ? onClose : handleBack}
              isDisabled={importing || currentStep === 4}
            >
              {currentStep === 0 ? 'Cancel' : 'Back'}
            </Button>
            {currentStep === 4 ? (
              <>
                <Button variant="outline" colorScheme="blue" onClick={resetState}>
                  Import Another File
                </Button>
                <Button colorScheme="blue" onClick={onClose}>Close</Button>
              </>
            ) : currentStep === 3 ? null : (
              <Button
                colorScheme="blue"
                onClick={handleNext}
                isDisabled={!canProceed() || importing}
                data-tour="studio-next"
              >
                {nextLabel}
              </Button>
            )}
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
